/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import { assign, cancel, createMachine, send } from 'xstate';
import { APIConstants } from '../../../../../../../constants/APIConstants';
import ExtJS from '../../../../../../../interface/ExtJS';
import { fetchEvaluations } from '../evaluations.api';
import { EVALUATIONS_STRINGS as STRINGS } from '../evaluations.strings';
import { DEFAULT_QUERY, type EvaluationsPage, type EvaluationsQuery } from '../evaluations.types';
import { pollDelayMs } from '../repositoryReevaluation.constants';

export interface EvaluationsListContext {
  query: EvaluationsQuery;
  data: EvaluationsPage | null;
  error: string | null;
  /** What the search box shows: typed immediately, committed to the URL after a pause. */
  searchInput: string;
  /** The last search this machine committed, so only a change from elsewhere resets the typed text. */
  committedSearch: string;
  /** Loads in a row that found a re-evaluation running, which sets how long the next follow-up load waits. */
  followAttempt: number;
  /** A follow-up load failed: the rows stay as they were and the page stops following until a load succeeds. */
  followStopped: boolean;
}

export type EvaluationsListEvent =
  | { type: 'SET_QUERY'; query: EvaluationsQuery }
  | { type: 'TYPE_SEARCH'; value: string }
  | { type: 'COMMIT_SEARCH' }
  | { type: 'RESET_SEARCH' }
  | { type: 'RETRY' }
  | { type: 'REFRESH' };

type EventOf<T extends EvaluationsListEvent['type']> = Extract<EvaluationsListEvent, { type: T }>;

const SEARCH_DEBOUNCE_ID = 'searchDebounce';

const setQuery = assign<EvaluationsListContext, EventOf<'SET_QUERY'>>({
  query: (_context, event) => event.query,
});

// A search in the URL that this machine did not commit came from elsewhere (clear filters, back): show it.
const syncSearch = assign<EvaluationsListContext, EventOf<'SET_QUERY'>>({
  searchInput: (context, event) => (event.query.search !== context.committedSearch ? event.query.search : context.searchInput),
  committedSearch: (context, event) =>
    event.query.search !== context.committedSearch ? event.query.search : context.committedSearch,
});

const typeSearch = assign<EvaluationsListContext, EventOf<'TYPE_SEARCH'>>({
  searchInput: (_context, event) => event.value,
});
const markSearchCommitted = assign<EvaluationsListContext>({
  committedSearch: (context) => context.searchInput.trim(),
});
const resetSearch = assign<EvaluationsListContext>({ searchInput: () => '', committedSearch: () => '' });

const hasRunningRow = (page: EvaluationsPage | null) => page?.results.some((row) => row.reevaluationRunning) ?? false;

// The previous rows are kept while loading, so a reload does not flash the empty state.
const setLoaded = assign<EvaluationsListContext, { type: 'done.invoke.fetchEvaluations'; data: EvaluationsPage }>({
  data: (_context, event) => event.data,
  error: () => null,
  followAttempt: (context, event) => (hasRunningRow(event.data) ? context.followAttempt + 1 : 0),
  followStopped: () => false,
});
const stopFollowing = assign<EvaluationsListContext>({ followStopped: () => true });
const setFailed = assign<EvaluationsListContext, { type: 'error.platform.fetchEvaluations'; data: unknown }>({
  data: () => null,
  error: (_context, event) => (event.data instanceof Error ? event.data.message : STRINGS.errors.loadList),
});

// Handled in every state: re-entering `loading` cancels the in-flight request, and the same query is a no-op.
// REFRESH loads the current query again (a re-evaluation ended), so it restarts a load instead of being dropped.
const queryChanges = {
  SET_QUERY: { target: 'loading', actions: ['setQuery', 'syncSearch'], cond: 'queryChanged' },
  REFRESH: { target: 'loading' },
};

export const evaluationsListMachine = createMachine<EvaluationsListContext, EvaluationsListEvent>(
  {
    id: 'evaluationsList',
    predictableActionArguments: true,
    initial: 'loading',
    context: {
      query: DEFAULT_QUERY,
      data: null,
      error: null,
      searchInput: '',
      committedSearch: '',
      followAttempt: 0,
      followStopped: false,
    },
    on: {
      TYPE_SEARCH: {
        actions: [
          cancel(SEARCH_DEBOUNCE_ID),
          'typeSearch',
          send<EvaluationsListContext, EvaluationsListEvent>('COMMIT_SEARCH', {
            delay: APIConstants.DEBOUNCE_DELAY,
            id: SEARCH_DEBOUNCE_ID,
          }),
        ],
      },
      COMMIT_SEARCH: { cond: 'draftNotCommitted', actions: ['markSearchCommitted', 'commitSearch'] },
      RESET_SEARCH: { actions: [cancel(SEARCH_DEBOUNCE_ID), 'resetSearch'] },
    },
    states: {
      loading: {
        invoke: {
          id: 'fetchEvaluations',
          src: 'fetchEvaluations',
          onDone: { target: 'loaded', actions: 'setLoaded' },
          onError: { target: 'failed', actions: 'setFailed' },
        },
        on: queryChanges,
      },
      // While a repository is being re-evaluated the page follows it: load again on the poll schedule until none is.
      loaded: { on: queryChanges, after: { LIST_FOLLOW_DELAY: { target: 'following', cond: 'shouldFollow' } } },
      // A background reload nobody asked for: it keeps the rows on screen, and if it fails it stops following instead
      // of replacing the list with the error screen (only a load the user asked for can fail the page).
      following: {
        invoke: {
          id: 'fetchEvaluations',
          src: 'fetchEvaluations',
          onDone: { target: 'loaded', actions: 'setLoaded' },
          onError: { target: 'loaded', actions: 'stopFollowing' },
        },
        on: queryChanges,
      },
      // No automatic retry: only RETRY or a new query leaves this state.
      failed: { on: { ...queryChanges, RETRY: 'loading' } },
    },
  },
  {
    services: {
      fetchEvaluations: async (context) => {
        await ExtJS.waitForPermissions();
        return fetchEvaluations(context.query);
      },
    },
    guards: {
      queryChanged: (context, event) =>
        event.type === 'SET_QUERY' && JSON.stringify(event.query) !== JSON.stringify(context.query),
      draftNotCommitted: (context) => context.searchInput.trim() !== context.committedSearch,
      shouldFollow: (context) => !context.followStopped && hasRunningRow(context.data),
    },
    delays: {
      LIST_FOLLOW_DELAY: (context) => pollDelayMs(Math.max(0, context.followAttempt - 1)),
    },
    actions: {
      setQuery,
      syncSearch,
      typeSearch,
      markSearchCommitted,
      resetSearch,
      setLoaded,
      setFailed,
      stopFollowing,
      // The hook provides this: it writes the committed search to the URL.
      commitSearch: () => undefined,
    },
  }
);
